import { useState } from 'react';
import type { ColorLista } from '../../../api/ops/listas';
import { Boton } from '../../../componentes/Boton';
import { Campo } from '../../../componentes/Campo';
import { Desplegable } from '../../../componentes/Desplegable';
import { Interruptor } from '../../../componentes/Interruptor';
import { OpcionRadio } from '../../../componentes/OpcionRadio';
import { Contador } from '../../../componentes/Contador';
import { Bloque, Botones, Caja, FechaHora, Fila } from '../comun';
import { COLORES, preguntaProhibida, type FormularioLista } from '../textos';

/**
 * Formulario de una lista o de una plantilla (los mismos ajustes; la plantilla no tiene cierre y su hora límite es solo la
 * hora, que se cuelga del día del evento). WhatsApp del formulario solo con «Enviar por WhatsApp» encendido (G562) y con
 * la confirmación del consumo. Las preguntas nunca piden teléfono ni salud (G49).
 */
const TIPOS = { text: 'Texto', date: 'Fecha', select: 'Elegir de una lista', boolean: 'Sí o no' } as const;

export function FormLista({ f, cambiar, errores, plantilla, whatsapp, movil, onCanalesDeEnvio }: { f: FormularioLista; cambiar: (c: Partial<FormularioLista>) => void; errores: Partial<Record<string, string>>; plantilla: boolean; whatsapp: boolean; movil: boolean; /** Abre «Mensajes › Canales de envío» (G562). */ onCanalesDeEnvio?: () => void }) {
    const [nueva, setNueva] = useState<{ etiqueta: string; tipo: 'text' | 'date' | 'boolean'; puerta: boolean; error: string } | null>(null);
    return (
        <>
            <Bloque>
                <Campo etiqueta="Nombre" valor={f.nombre} onChange={(v) => { cambiar({ nombre: v }); }} error={errores.nombre} max={70} nombre="nombre-lista" />
                <Desplegable etiqueta="Color" valor={f.color} onChange={(v) => { cambiar({ color: v as ColorLista }); }} opciones={Object.entries(COLORES).map(([k, v]) => ({ valor: k, texto: v[1] }))} />
            </Bloque>
            <Bloque titulo="Aforo y entrada">
                <Campo etiqueta="Aforo propio" valor={f.aforo} onChange={(v) => { cambiar({ aforo: v }); }} modo="numeric" error={errores.aforo} ayuda="Vacío = sin límite propio. Manda además el aforo del evento." />
                <Contador min={0} etiqueta="Acompañantes" valor={f.acompanantes} max={10} ayuda="Cuántos puede traer cada invitado. Cada uno lleva su pase." onCambio={(v) => { cambiar({ acompanantes: v }); }} />
                <FechaHora
                    etiqueta="Hora límite de entrada"
                    soloHora={plantilla}
                    valor={f.horaLimite}
                    onChange={(v) => { cambiar({ horaLimite: v }); }}
                    ayuda={plantilla ? 'Se cuelga del día del evento (pasa al día siguiente si cae antes del inicio).' : 'Fuera de hora, la puerta lo marca en ámbar. Vacía = sin hora límite.'}
                />
                {!plantilla && <FechaHora etiqueta="Cierre de la lista" valor={f.cierre} onChange={(v) => { cambiar({ cierre: v }); }} ayuda="Después nadie más se apunta. Nunca después del fin del evento. Vacío = al empezar el evento." />}
                <Campo etiqueta="Precio en puerta (€, opcional)" valor={f.puerta} onChange={(v) => { cambiar({ puerta: v }); }} modo="decimal" error={errores.puerta} ayuda="Informativo: sale en el pase. Vacío = gratis." />
            </Bloque>
            <Bloque titulo="Alta pública">
                <Interruptor etiqueta="Se pueden apuntar solos" valor={f.publica} onChange={(v) => { cambiar({ publica: v }); }} ayuda="Con el enlace de cada RRPP en Instagram y su formulario web." />
                {f.publica && (
                    <div className={`rv-ca-dos${movil ? ' rv-ca-dos--movil' : ''}`} role="radiogroup" aria-label="El formulario web envía el pase por">
                        <OpcionRadio elegida={f.envio === 'email'} onElegir={() => { cambiar({ envio: 'email', entendido: false }); }} titulo="Email" texto="No gasta nada" icono="mail" />
                        <OpcionRadio elegida={f.envio === 'whatsapp'} onElegir={() => { cambiar({ envio: 'whatsapp' }); }} titulo="WhatsApp" texto={whatsapp ? 'Gasta del paquete Servicio' : 'Apagado en Mensajes'} icono="message-circle" bloqueada={!whatsapp && f.envio !== 'whatsapp'} />
                    </div>
                )}
                {f.publica && !whatsapp && (
                    <Caja icono="message-circle">
                        Para ofrecer WhatsApp, enciende «Enviar por WhatsApp» en{' '}
                        {onCanalesDeEnvio === undefined ? 'Mensajes › Canales de envío' : <button type="button" className="rv-ls-mas rv-ls-mas--enlinea" onClick={onCanalesDeEnvio}>Mensajes › Canales de envío</button>}
                        . Mientras esté apagado, el pase del formulario sale por email.
                    </Caja>
                )}
                {f.publica && f.envio === 'whatsapp' && (
                    <>
                        <Caja icono="message-circle" tono="aviso" titulo="Consume tu paquete.">
                            Cada alta por el formulario gastará un mensaje del paquete Servicio de WhatsApp. El formulario pedirá su teléfono y tus RRPP lo verán en su portal. Por Instagram y por email no se gasta nada.
                        </Caja>
                        <Interruptor etiqueta="Entendido: cada pase por WhatsApp gasta un mensaje" valor={f.entendido} onChange={(v) => { cambiar({ entendido: v }); }} error={errores.entendido} />
                    </>
                )}
            </Bloque>
            <Bloque titulo="Preguntas al apuntarse" intro={f.campos.length === 0 ? 'Opcional. Por ejemplo, «Mesa preferida».' : undefined}>
                {f.campos.map((c, i) => (
                    <div key={`${c.label}-${String(i)}`} className="rv-ls-pregunta">
                        <Fila titulo={c.label} linea={`${TIPOS[c.kind]}${c.show_at_door === true ? ' · se ve en la puerta' : ''}`} baldosa="message-square-text" />
                        <button type="button" className="rv-ls-fantasma" aria-label={`Quitar la pregunta ${c.label}`} onClick={() => { cambiar({ campos: f.campos.filter((_, j) => j !== i) }); }}>
                            <span aria-hidden="true">×</span>
                        </button>
                    </div>
                ))}
                {nueva === null ? (
                    f.campos.length < 20 && (
                        <button type="button" className="rv-ls-mas" onClick={() => { setNueva({ etiqueta: '', tipo: 'text', puerta: false, error: '' }); }}>
                            + Añadir una pregunta
                        </button>
                    )
                ) : (
                    <>
                        <Campo etiqueta="Pregunta" valor={nueva.etiqueta} onChange={(v) => { setNueva({ ...nueva, etiqueta: v, error: '' }); }} max={70} error={nueva.error} ayuda="Nunca teléfono ni datos de salud." />
                        <Desplegable etiqueta="Tipo de respuesta" valor={nueva.tipo} onChange={(v) => { setNueva({ ...nueva, tipo: v as 'text' | 'date' | 'boolean' }); }} opciones={[{ valor: 'text', texto: 'Texto' }, { valor: 'date', texto: 'Fecha' }, { valor: 'boolean', texto: 'Sí o no' }]} />
                        <Interruptor etiqueta="Se ve en la puerta" valor={nueva.puerta} onChange={(v) => { setNueva({ ...nueva, puerta: v }); }} />
                        <Botones>
                            <Boton
                                tipo="secundario"
                                icono="plus"
                                onClick={() => {
                                    const t = nueva.etiqueta.trim();
                                    if (t.length < 2) {
                                        setNueva({ ...nueva, error: 'Escribe la pregunta.' });
                                    } else if (preguntaProhibida(t)) {
                                        setNueva({ ...nueva, error: 'No se puede preguntar por el teléfono ni por la salud.' });
                                    } else {
                                        cambiar({ campos: [...f.campos, { label: t, kind: nueva.tipo, required: false, show_at_door: nueva.puerta }] });
                                        setNueva(null);
                                    }
                                }}
                            >
                                Añadir la pregunta
                            </Boton>
                            <Boton tipo="fantasma" icono="x" onClick={() => { setNueva(null); }}>
                                Cancelar
                            </Boton>
                        </Botones>
                    </>
                )}
            </Bloque>
        </>
    );
}
